import React from 'react';
import routes from 'common/routes';
import { roles } from 'common/constants/options';
import { actions, dateRange, userProfile } from 'common/table/columns/customColumns';
import InvoiceCell from 'components/shared/table-columns/InvoiceCell';

/**
 * Generates columns configuration for client payment table.
 *
 * @param  {Object}    params                Parameters for configuring columns.
 * @param  {Array}     params.data           Data used in the table.
 * @param  {String}    params.role           User role.
 * @param  {Function}  params.translateFunc  Function to translate labels.
 *
 * @returns  {Array}  Array of column configurations.
 */
export const getClientPaymentColumns = ({ data, role, translateFunc }) => {
  const columns = [
    { label: translateFunc('voucherNumber', { ns: 'labels' }), name: 'id' },
    userProfile({ data, label: translateFunc('client', { count: 1, ns: 'labels' }) }),
    { label: translateFunc('amount', { ns: 'labels' }), name: 'price' },
    { label: translateFunc('plan', { ns: 'labels' }), name: 'plan' },
    dateRange({ data, label: translateFunc('invoicePeriod', { ns: 'labels' }) }),
    { label: translateFunc('paymentDate', { ns: 'labels' }), name: 'created_at' }
  ];

  if (role !== roles.supervisor) {
    columns.push(
      actions({
        items: ['edit'],
        data,
        label: translateFunc('actions', { ns: 'labels' }),
        actionRoutes: { edit: routes.editClientPayment.path }
      })
    );
  }

  return columns;
};

/**
 * Generates basic columns configuration for client payment table.
 *
 * @param  {Object}    params                Parameters for configuring columns.
 * @param  {Array}     params.data           Data used in the table.
 * @param  {Function}  params.translateFunc  Function to translate labels.
 *
 * @returns  {Array}  Array of basic column configurations.
 */
export const getClientPaymentBasicColumns = ({ data, translateFunc }) => [
  { label: translateFunc('client', { count: 1, ns: 'labels' }), name: 'user' },
  { label: translateFunc('sessionsPaid', { ns: 'labels' }), name: 'sessions_paid' },
  dateRange({ data, label: translateFunc('invoicePeriod', { ns: 'labels' }) }),
  { label: translateFunc('paymentDate', { ns: 'labels' }), name: 'created_at' },
  { label: translateFunc('amount', { ns: 'labels' }), name: 'price' },
  { label: translateFunc('currency', { ns: 'labels' }), name: 'currency' }
];

/**
 * Generates columns configuration for my client payment table.
 *
 * @param  {Object}    params                Parameters for configuring columns.
 * @param  {Array}     params.data           Data used in the table.
 * @param  {Function}  params.translateFunc  Function to translate labels.
 *
 * @returns  {Array}  Array of column configurations for personal use.
 */
export const getMyClientPaymentColumns = ({ data, translateFunc }) => [
  { label: translateFunc('voucherNumber', { ns: 'labels' }), name: 'id' },
  { label: translateFunc('amount', { ns: 'labels' }), name: 'price' },
  { label: translateFunc('plan', { ns: 'labels' }), name: 'plan' },
  dateRange({ data, label: translateFunc('invoicePeriod', { ns: 'labels' }) }),
  { label: translateFunc('paymentDate', { ns: 'labels' }), name: 'created_at' },
  {
    label: translateFunc('invoice', { ns: 'labels' }),
    name: 'invoice_url',
    options: {
      customBodyRenderLite: (dataIndex) => {
        const row = data[dataIndex];
        return <InvoiceCell url={row?.invoice_url} label={translateFunc('invoice', { ns: 'labels' })} />;
      }
    }
  }
];
